import { v4 as uuid } from 'uuid';
import * as Yup from 'yup';
import { isValidPhone } from '@rsos/base-utils/validators';
import { isFlagged } from '@rsos/flags';
import {
  formatInternationalPhoneNumberHuman,
  isValidInternationalPhoneNumber,
} from '@rsos/utils/internationalPhoneNumberUtils';

export interface ValidationResult {
  isValid: boolean;
  type: 'email' | 'phone' | null;
}

export interface TagObject {
  value: string;
  isValid: boolean;
  id: string;
  type?: string;
  error?: string;
}

export const validationSchemaEmails = Yup.object().shape({
  emailsCumulative: Yup.array().test(
    'isEmailsValid',
    'Invalid email address.',
    function (value) {
      const { path, createError } = this;
      const result = value?.find(v => !v.isValid)
        ? createError({ path, message: 'Invalid email(s) address.' })
        : true;
      return result;
    },
  ),
});

export const validationSchemaPhoneNumbers = Yup.object().shape({
  phoneNumbersCumulative: Yup.array().test(
    'isPhoneNumberValid',
    'Invalid phone number.',
    function (value) {
      const { path, createError } = this;
      const result = value?.find(v => !v.isValid)
        ? createError({ path, message: 'Invalid phone number(s).' })
        : true;
      return result;
    },
  ),
});

export const phoneNumberValidator = (val: string): boolean => {
  const sanitizedValue = val.replace(/[()-\s]/g, '');
  return isValidPhone(sanitizedValue);
};

/**
 *
 * Utility function to format a phone number to look like 1 (347) 771-0345
 * @param {String} value - phone number
 *
 * it can be used on the fly while typing or on paste. This utility function for numbers that start with 1
 * thus the formatting needs to account for an additional prefix number
 */

export const formatStartsWithOneNumber = (value: string): string => {
  let formattedNumber = value;

  /* function to sanitize the input value */
  const regex = (): string => value.replace(/[^0-9]+/g, '');
  /* function to separate out US identifier - 1 */
  const prefix = (): string => `${regex().slice(0, 1)}`;
  /* function to separate out the area code and place it inside () */
  const areaCode = (): string => `(${regex().slice(1, 4)})`;
  /* function to separate out first 7 digits that go before the dash */
  const firstSeven = (): string =>
    `${prefix()} ${areaCode()} ${regex().slice(4, 7)}`;

  /* length of the entire string: digits with spaces, dashes and () */
  const stringLength = value?.length;
  /* length of the actual digits  */
  const digitsLength = regex().length;
  /* Dynamic trail as user types */
  const trailer = (start: number): string =>
    `${regex().slice(start, digitsLength)}`;

  if (stringLength < 4) {
    /* sanitizes first 3 characters */
    formattedNumber = regex();
  } else if (stringLength === 5) {
    /* applies formatting after area code and prefix */
    formattedNumber = `${prefix()} ${areaCode()} ${trailer(4)}`;
  } else if (stringLength === 7) {
    /* when removing digits, area code () have to be removed */
    formattedNumber = `${prefix()} ${areaCode().replace(/[()]/g, '')}`;
    /* have to account for both lengths as it doesn't distunguish between adding vs removing characters */
  } else if (stringLength >= 8 && stringLength <= 11 && digitsLength !== 11) {
    /* before dash */
    formattedNumber = `${prefix()} ${areaCode()} ${trailer(4)}`;
    /* have to account for both lengths as it doesn't distunguish between adding vs removing characters */
  } else if (stringLength >= 12 || digitsLength === 11) {
    /* after dash */
    formattedNumber = `${firstSeven()}-${trailer(7)}`;
  } else if (digitsLength === 4) {
    // After area code
    formattedNumber = `${prefix()} ${areaCode()} `;
  }
  return formattedNumber;
};

/**
 *
 * Utility function to format a phone number to look like (347) 771-0345
 * @param {String} value - phone number
 *
 * it can be used on the fly while typing or on paste. This utility function for numbers that don't start with 1
 */

export const formatPhoneNumber = (value: string): string => {
  let formattedNumber = value;

  /* function to sanitize the input value */
  const regex = (): string => value.replace(/[^0-9]+/g, '');
  /* function to separate out the area code and place it inside () */
  const areaCode = (): string => `(${regex().slice(0, 3)})`;
  /* function to separate out first 6 digits that go before the dash */
  const firstSix = (): string => `${areaCode()} ${regex().slice(3, 6)}`;

  /* length of the entire string: digits with spaces, dashes and () */
  const stringLength = value?.length;
  /* length of the actual digits  */
  const digitsLength = regex().length;
  /* Dynamic trail as user types */
  const trailer = (start: number): string =>
    `${regex().slice(start, digitsLength)}`;

  if (stringLength <= 3) {
    /* sanitizes first 3 characters */
    formattedNumber = regex();
  } else if (stringLength === 4) {
    /* applies formatting after area code */
    formattedNumber = `${areaCode()} ${trailer(3)}`;
  } else if (stringLength === 5) {
    /* when removing digits, area code () have to be removed */
    formattedNumber = `${areaCode().replace(/[()]/g, '')}`;
  } else if (stringLength > 5 && stringLength <= 9) {
    /* before dash */
    formattedNumber = `${areaCode()} ${trailer(3)}`;
  } else if (stringLength >= 10) {
    /* after dash */
    formattedNumber = `${firstSix()}-${trailer(6)}`;
  }
  return formattedNumber;
};

/**
 *
 * Utility function to validate an input value to convert it to a tag
 * @param {String} value - input value (currently expects a phone number or an email)
 * @param {String} inputType - now we have a `tel` indicating it is a phone number or it falls back to
 * else, currently it is for emails
 * @param {Function} validator - validator function to be applied to validate the value
 * @returns {Object} - an object with a shape of {
          value: string value,
          isValid: boolean,
          id: unique id,
        };
 */
export const getValidatedNewTag = (
  value: string,
  inputType?: string,
  validator?: (value: string) => boolean,
): TagObject => {
  let validatedNewTag: TagObject;
  const v = value.trim();
  if (inputType === 'tel') {
    if (isFlagged('international_sms')) {
      return {
        value: formatInternationalPhoneNumberHuman(v),
        isValid: isValidInternationalPhoneNumber(v),
        id: uuid(),
      };
    }
    return {
      value: v.startsWith('1')
        ? formatStartsWithOneNumber(v)
        : formatPhoneNumber(v),
      isValid: validator ? validator(v.startsWith('1') ? v : `1${v}`) : false,
      id: uuid(),
    };
  } else {
    validatedNewTag = {
      value: v,
      isValid: validator ? validator(v) : false,
      id: uuid(),
    };
  }
  return validatedNewTag;
};

// ===== MIXED EMAIL/PHONE VALIDATION FUNCTIONS =====

/**
 * Enhanced phone validator for mixed input that handles 10-digit numbers
 */
const validatePhoneForMixed = (value: string): boolean => {
  let sanitizedValue = value.replace(/[()-\s]/g, '');

  // Add leading 1 for 10-digit North American numbers
  if (sanitizedValue.length === 10 && /^[2-9]/.test(sanitizedValue)) {
    sanitizedValue = '1' + sanitizedValue;
  }

  return isValidPhone(sanitizedValue);
};
/**
 * Validates if a value is an email or phone number for mixed mode
 * @param {String} value - input value to validate
 * @returns {Object} - { isValid: boolean, type: 'email' | 'phone' | null }
 */
export const validateMixedEntry = (value: string): ValidationResult => {
  const trimmedValue = value.trim();

  // Return false for empty or whitespace-only strings
  if (!trimmedValue) {
    return { isValid: false, type: null };
  }

  if (Yup.string().email().isValidSync(trimmedValue)) {
    return { isValid: true, type: 'email' };
  }
  if (validatePhoneForMixed(trimmedValue)) {
    return { isValid: true, type: 'phone' };
  }
  return { isValid: false, type: null };
};

/**
 * Enhanced validation schema for mixed email/phone entries
 */
export const validationSchemaMixed = Yup.object().shape({
  mixedCumulative: Yup.array().test(
    'isMixedValid',
    'Invalid email or phone number.',
    function (value) {
      const { path, createError } = this;
      const result = value?.find(v => !v.isValid)
        ? createError({ path, message: 'Invalid email(s) or phone number(s).' })
        : true;
      return result;
    },
  ),
});

/**
 * Enhanced getValidatedNewTag that supports mixed email/phone validation
 * @param {String} value - input value
 * @param {String} inputType - 'tel', 'email', or 'mixed'
 * @param {Function} validator - validator function
 * @returns {Object} - tag object with type information
 */
export const getValidatedNewTagEnhanced = (
  value: string,
  inputType?: string,
  validator?: (value: string) => boolean,
): TagObject => {
  const v = value.trim();

  // Handle mixed mode
  if (inputType === 'mixed') {
    const { isValid, type } = validateMixedEntry(v);
    return {
      value: v,
      isValid,
      type: type || 'invalid',
      id: uuid(),
      error: !isValid ? 'Invalid email or phone number format' : undefined,
    };
  }

  // Fall back to original behavior for backward compatibility
  return getValidatedNewTag(value, inputType, validator);
};
